import type { BrandConfig } from "@cfg/brand-sdk";
import { User, UserManager, WebStorageStateStore } from "oidc-client-ts";
import { useEffect, useState } from "react";

/**
 * OpenID Connect (authorization code + PKCE) against the Cognito user pool.
 * Google sign-in is federated inside Cognito, so the shell only knows OIDC.
 * When the brand has no `auth` block (local dev), auth is simply unavailable.
 */
export interface AuthUser {
  sub: string;
  name?: string;
  email?: string;
  picture?: string;
}

export class Auth {
  private manager: UserManager | null;
  private listeners = new Set<() => void>();
  user: AuthUser | null = null;
  private raw: User | null = null;
  /** `VITE_DEV_USER` on the dev server: a fake session whose token the local API accepts as "dev:<sub>[:staff]". */
  private readonly devUser = import.meta.env.DEV ? (import.meta.env.VITE_DEV_USER ?? null) : null;

  constructor(config: BrandConfig) {
    if (!config.auth) {
      this.manager = null;
      if (this.devUser) this.user = { sub: this.devUser.split(":")[0], name: this.devUser.split(":")[0] };
      return;
    }
    const origin = window.location.origin;
    this.manager = new UserManager({
      authority: config.auth.authority,
      client_id: config.auth.clientId,
      redirect_uri: `${origin}/auth/callback`,
      post_logout_redirect_uri: origin,
      response_type: "code",
      scope: "openid email profile",
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      automaticSilentRenew: true,
      // Cognito's logout endpoint differs from the OIDC end_session default.
      metadataSeed: config.auth.domain
        ? { end_session_endpoint: `${config.auth.domain}/logout?client_id=${config.auth.clientId}&logout_uri=${encodeURIComponent(origin)}` }
        : undefined,
    });
    this.manager.events.addUserLoaded((u) => this.setUser(u));
    this.manager.events.addUserUnloaded(() => this.setUser(null));
  }

  get enabled(): boolean {
    return this.manager !== null || this.devUser !== null;
  }

  private setUser(u: User | null) {
    this.raw = u;
    this.user = u && !u.expired ? { sub: u.profile.sub, name: u.profile.name, email: u.profile.email, picture: u.profile.picture } : null;
    for (const l of this.listeners) l();
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  async init(): Promise<void> {
    if (!this.manager) return;
    if (window.location.pathname === "/auth/callback") {
      try {
        const u = await this.manager.signinCallback();
        this.setUser(u ?? null);
        const returnTo = (u?.state as { returnTo?: string } | undefined)?.returnTo ?? "/";
        window.history.replaceState({}, "", returnTo);
      } catch (err) {
        console.error("Sign-in callback failed", err);
        window.history.replaceState({}, "", "/");
      }
      return;
    }
    const u = await this.manager.getUser();
    this.setUser(u);
  }

  signIn(returnTo = window.location.pathname + window.location.search): Promise<void> {
    if (!this.manager && this.devUser) {
      this.user = { sub: this.devUser.split(":")[0], name: this.devUser.split(":")[0] };
      for (const l of this.listeners) l();
      return Promise.resolve();
    }
    if (!this.manager) return Promise.reject(new Error("Sign-in is not configured for this deployment"));
    return this.manager.signinRedirect({ state: { returnTo } });
  }

  signOut(): Promise<void> {
    if (!this.manager) {
      this.user = null;
      for (const l of this.listeners) l();
      return Promise.resolve();
    }
    return this.manager.signoutRedirect();
  }

  async getToken(): Promise<string | null> {
    if (!this.manager) return this.devUser && this.user ? `dev:${this.devUser}` : null;
    if (this.raw && !this.raw.expired) return this.raw.id_token ?? null;
    const u = await this.manager.getUser();
    this.setUser(u);
    return u && !u.expired ? (u.id_token ?? null) : null;
  }
}

export function useAuthUser(auth: Auth): AuthUser | null {
  const [user, setUser] = useState(auth.user);
  useEffect(() => auth.subscribe(() => setUser(auth.user)), [auth]);
  return user;
}
